import React, { useState, useRef, useEffect } from 'react';
import { Page } from '../../../types';
import './PreviewHorizontalSlide.css';

const globalSlideStates: Record<string, {
  currentSlideIndex: number;
  offset: number;
  containerWidth: number;
}> = {};

export const getGlobalSlideState = (pageId: string) => {
  return globalSlideStates[pageId] || {
    currentSlideIndex: 0,
    offset: 0,
    containerWidth: 0
  };
};

const setGlobalSlideState = (pageId: string, state: Partial<{
  currentSlideIndex: number;
  offset: number;
  containerWidth: number;
}>) => {
  globalSlideStates[pageId] = {
    ...getGlobalSlideState(pageId),
    ...state
  };
};

const shouldResetSlideState = (pageId: string, initialIndex: number = 1) => {
  const state = getGlobalSlideState(pageId);
  const targetIndex = initialIndex - 1;
  return state.currentSlideIndex !== targetIndex;
};

const resetSlideState = (pageId: string, initialIndex: number = 1) => {
  if (shouldResetSlideState(pageId, initialIndex)) {
    setGlobalSlideState(pageId, {
      currentSlideIndex: initialIndex - 1,
      offset: initialIndex - 1
    });
  }
};

interface PreviewHorizontalSlideProps {
  slides: Partial<Page>[];
  className?: string;
  slideWidth?: number;
  viewportWidth?: number;
  indicatorPosition?: 'top' | 'bottom';
  infiniteLoop?: boolean;
  initialIndex?: number;
  positionAdjustment?: number;
  indicatorOffsetX?: number;
  indicatorOffsetY?: number;
  pageId?: string;
  onPageChange?: () => void;
}

export const PreviewHorizontalSlide: React.FC<PreviewHorizontalSlideProps> = ({
  slides,
  className = '',
  slideWidth = 100,
  viewportWidth,
  indicatorPosition = 'bottom',
  infiniteLoop = true,
  initialIndex = 1,
  positionAdjustment = 0,
  indicatorOffsetX = 0,
  indicatorOffsetY = 0,
  pageId,
  onPageChange
}) => {
  const effectiveViewportWidth = viewportWidth ?? 100;
  const validSlides = slides.filter((slide): slide is Partial<Page> & { backgroundImage: string } => 
    slide && slide.backgroundImage !== undefined
  );
  
  const totalSlides = validSlides.length;
  
  const displaySlides = totalSlides > 0 && infiniteLoop ? [
    ...validSlides,
    ...validSlides,
    ...validSlides,
    ...validSlides
  ] : validSlides;

  const globalState = pageId ? getGlobalSlideState(pageId) : null;
  
  const calculatedInitialIndex = Math.max(0, Math.min((initialIndex - 1) || 0, totalSlides - 1));
  
  const [internalCurrentSlideIndex, setInternalCurrentSlideIndex] = useState(
    globalState?.currentSlideIndex ?? calculatedInitialIndex
  );
  const [internalOffset, setInternalOffset] = useState(
    globalState?.offset ?? calculatedInitialIndex
  );
  const [internalContainerWidth, setInternalContainerWidth] = useState(
    globalState?.containerWidth ?? 0
  );
  
  const currentSlideIndex = globalState?.currentSlideIndex ?? internalCurrentSlideIndex;
  const offset = globalState?.offset ?? internalOffset;
  const containerWidth = globalState?.containerWidth ?? internalContainerWidth;
  
  const [isTransitioning, setIsTransitioning] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const prevIndexRef = useRef(currentSlideIndex);
  const prevPageIdRef = useRef(pageId);
  const containerWidthRef = useRef(internalContainerWidth);
  const isUpdatingWidthRef = useRef(false);
  
  const currentSlideIndexRef = useRef(currentSlideIndex);
  useEffect(() => {
    currentSlideIndexRef.current = currentSlideIndex;
  }, [currentSlideIndex]);
  
  const initialIndexApplied = useRef(false);
  useEffect(() => {
    if (totalSlides > 0 && !initialIndexApplied.current && pageId) {
      const targetIndex = Math.max(0, Math.min(initialIndex - 1, totalSlides - 1));
      const currentState = getGlobalSlideState(pageId);
      if (currentState.currentSlideIndex !== targetIndex) {
        setCurrentSlideIndex(targetIndex);
      }
      initialIndexApplied.current = true;
    }
  }, [totalSlides, initialIndex, pageId]);

  useEffect(() => {
    if (pageId && pageId !== prevPageIdRef.current) {
      if (onPageChange) {
        onPageChange();
      }
      prevPageIdRef.current = pageId;
    }
  }, [pageId, onPageChange]);
  
  useEffect(() => {
    const handleReset = (event: CustomEvent) => {
      const targetPageId = event.detail?.pageId;
      if (targetPageId && pageId && targetPageId === pageId) {
        resetSlideState(pageId, initialIndex);
      }
    };
    
    window.addEventListener('partialSlideReset', handleReset as EventListener);
    
    return () => {
      window.removeEventListener('partialSlideReset', handleReset as EventListener);
    };
  }, [pageId, initialIndex]);
  
  useEffect(() => {
    const handleNext = (event: CustomEvent) => {
      const targetPageId = event.detail?.pageId;
      
      if (targetPageId && pageId && targetPageId === pageId) {
        const current = currentSlideIndexRef.current;
        
        const nextIndex = infiniteLoop 
          ? current + 1 
          : Math.min(current + 1, totalSlides - 1);
        
        if (pageId) {
          setGlobalSlideState(pageId, { currentSlideIndex: nextIndex });
        }
        setInternalCurrentSlideIndex(nextIndex);
        currentSlideIndexRef.current = nextIndex;
      }
    };
    
    window.addEventListener('partialSlideNext', handleNext as EventListener);
    
    return () => {
      window.removeEventListener('partialSlideNext', handleNext as EventListener);
    };
  }, [pageId, infiniteLoop, totalSlides]);
  
  const setCurrentSlideIndex = (index: number | ((prev: number) => number)) => {
    const indexValue = typeof index === 'function' ? index(currentSlideIndex) : index;
    if (pageId) {
      setGlobalSlideState(pageId, { currentSlideIndex: indexValue });
    }
    setInternalCurrentSlideIndex(indexValue);
  };
  
  const setOffset = (newOffset: number | ((prev: number) => number)) => {
    const offsetValue = typeof newOffset === 'function' ? newOffset(offset) : newOffset;
    if (pageId) {
      setGlobalSlideState(pageId, { offset: offsetValue });
    }
    setInternalOffset(offsetValue);
  };
  
  const pendingWidthRef = useRef<number | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    const element = containerRef.current;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;

      const width = entry.contentRect.width;
      if (width <= 0) return;

      const currentWidth = pageId ? getGlobalSlideState(pageId).containerWidth : containerWidthRef.current;
      if (Math.abs(currentWidth - width) > 1) {
        pendingWidthRef.current = width;
        containerWidthRef.current = width;
        if (pageId) {
          setGlobalSlideState(pageId, { containerWidth: width });
        }
      }
    });
    
    observer.observe(element);
    
    return () => {
      observer.disconnect();
    };
  }, [pageId]);

  useEffect(() => {
    if (pendingWidthRef.current !== null) {
      const width = pendingWidthRef.current;
      pendingWidthRef.current = null;
      setInternalContainerWidth(width);
    }
  });
  
  useEffect(() => {
    if (totalSlides === 0) return;
    
    const diff = currentSlideIndex - prevIndexRef.current;
    if (diff === 0) return;
    
    prevIndexRef.current = currentSlideIndex;
    
    if (infiniteLoop) {
      setIsTransitioning(true);
      setOffset(prev => prev + diff);
      
      const timer = setTimeout(() => {
        const normalizedIndex = ((currentSlideIndex % totalSlides) + totalSlides) % totalSlides;
        requestAnimationFrame(() => {
          setIsTransitioning(false);
          requestAnimationFrame(() => {
            setOffset(normalizedIndex);
          });
        });
      }, 850);
      
      return () => clearTimeout(timer);
    } else {
      setIsTransitioning(true);
      setOffset(currentSlideIndex);
      
      const timer = setTimeout(() => {
        setIsTransitioning(false);
      }, 850);
      
      return () => clearTimeout(timer);
    }
  }, [currentSlideIndex, totalSlides, infiniteLoop]);

  const goToNext = () => {
    if (infiniteLoop) {
      setCurrentSlideIndex(prev => prev + 1);
    } else {
      setCurrentSlideIndex(prev => Math.min(prev + 1, totalSlides - 1));
    }
  };

  const goToPrevious = () => {
    if (infiniteLoop) {
      setCurrentSlideIndex(prev => prev - 1);
    } else {
      setCurrentSlideIndex(prev => Math.max(prev - 1, 0));
    }
  };

  const [startPos, setStartPos] = useState<{ x: number; y: number } | null>(null);
  const [endPos, setEndPos] = useState<{ x: number; y: number } | null>(null);

  const minSwipeDistance = 50;

  const handleStart = (clientX: number, clientY: number) => {
    setEndPos(null);
    setStartPos({ x: clientX, y: clientY });
  };

  const handleMove = (clientX: number, clientY: number) => {
    setEndPos({ x: clientX, y: clientY });
  };

  const handleEnd = () => {
    if (!startPos || !endPos) return;
    
    const distanceX = startPos.x - endPos.x;
    const distanceY = startPos.y - endPos.y;
    const isLeftSwipe = distanceX > minSwipeDistance;
    const isRightSwipe = distanceX < -minSwipeDistance;
    const isVerticalSwipe = Math.abs(distanceY) > Math.abs(distanceX);

    if (isLeftSwipe && !isVerticalSwipe) {
      goToNext();
    }
    if (isRightSwipe && !isVerticalSwipe) {
      goToPrevious();
    }
  };

  const onTouchStart = (e: React.TouchEvent) => {
    e.preventDefault();
    handleStart(e.targetTouches[0].clientX, e.targetTouches[0].clientY);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    e.preventDefault();
    handleMove(e.targetTouches[0].clientX, e.targetTouches[0].clientY);
  };

  const onTouchEnd = (e: React.TouchEvent) => {
    e.preventDefault();
    handleEnd();
  };

  const onMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    handleStart(e.clientX, e.clientY);
  };

  const onMouseMove = (e: React.MouseEvent) => {
    if (startPos) {
      e.preventDefault();
      handleMove(e.clientX, e.clientY);
    }
  };

  const onMouseUp = (e: React.MouseEvent) => {
    e.preventDefault();
    handleEnd();
  };

  if (validSlides.length === 0) {
    return (
      <div className="preview-horizontal-slide-wrapper">
        <div className={`preview-horizontal-slide-container empty ${className}`}>
          <div className="empty-slide">
            <div className="empty-placeholder">
              <span>スライドが設定されていません</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (validSlides.length === 1) {
    const hasViewportWidth = viewportWidth !== undefined;
    const itemWidth = hasViewportWidth ? effectiveViewportWidth : slideWidth;
    const imageWidth = hasViewportWidth ? slideWidth : 100;
    const horizontalPadding = hasViewportWidth ? ((effectiveViewportWidth - slideWidth) / 2) : 0;
    
    return (
      <div className="preview-horizontal-slide-wrapper">
        <div 
          ref={containerRef}
          className={`preview-horizontal-slide-container single ${className}`}
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            overflow: 'hidden',
          }}
        >
          <div style={{ 
            width: `${itemWidth}%`,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            paddingLeft: hasViewportWidth ? `${horizontalPadding}%` : 0,
            paddingRight: hasViewportWidth ? `${horizontalPadding}%` : 0,
            boxSizing: 'border-box',
          }}>
            <img
              src={validSlides[0].backgroundImage}
              alt="Slide 1"
              className="preview-slide-image"
              style={{
                width: hasViewportWidth ? `${(imageWidth / itemWidth) * 100}%` : '100%',
                height: 'auto',
                maxHeight: '100%',
                objectFit: 'contain',
              }}
            />
          </div>
        </div>
      </div>
    );
  }

  const displayIndex = ((currentSlideIndex % totalSlides) + totalSlides) % totalSlides;

  return (
    <div 
      className={`preview-horizontal-slide-wrapper indicator-${indicatorPosition}`}
      style={{
        paddingTop: positionAdjustment > 0 ? `${positionAdjustment}px` : 0,
        paddingBottom: positionAdjustment < 0 ? `${Math.abs(positionAdjustment)}px` : 0
      }}
    >
      <div 
        ref={containerRef}
        className={`preview-horizontal-slide-container ${className}`}
        style={{
          touchAction: 'none',
          overflow: 'hidden',
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          position: 'relative',
        }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
      >
        <div
          className="preview-slides-wrapper"
          style={{
            transform: containerWidth > 0 
              ? (() => {
                  const slideOffset = containerWidth * (effectiveViewportWidth / 100);
                  const centeringOffset = containerWidth / 2 - slideOffset / 2;
                  
                  if (infiniteLoop) {
                    return `translateX(${-(offset + totalSlides) * slideOffset + centeringOffset}px)`;
                  } else {
                    return `translateX(${-offset * slideOffset + centeringOffset}px)`;
                  }
                })()
              : 'none',
            transition: isTransitioning ? 'transform 0.8s cubic-bezier(0.25, 0.1, 0.25, 1)' : 'none',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {displaySlides.map((slide, index) => {
            const hasViewportWidth = viewportWidth !== undefined;
            const itemWidth = effectiveViewportWidth;
            const imageWidthPercent = hasViewportWidth ? (slideWidth / effectiveViewportWidth) * 100 : slideWidth;
            return (
              <div
                key={`slide-${slide.id || index}-${index}`}
                className="preview-slide-item"
                style={{
                  width: containerWidth > 0 ? `${containerWidth * (itemWidth / 100)}px` : `${itemWidth}%`,
                  minWidth: containerWidth > 0 ? `${containerWidth * (itemWidth / 100)}px` : `${itemWidth}%`,
                  position: 'relative',
                  flexShrink: 0,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  boxSizing: 'border-box',
                }}
              >
                <img
                  src={slide.backgroundImage}
                  alt={`Slide ${index + 1}`}
                  className="preview-slide-image"
                  loading={index === 0 ? 'eager' : 'lazy'}
                  style={{
                    width: `${imageWidthPercent}%`,
                    height: 'auto',
                    maxHeight: '100%',
                    objectFit: 'contain',
                  }}
                />
              </div>
            );
          })}
        </div>

      </div>

      {validSlides.length > 1 && indicatorPosition === 'top' && (
        <div 
          className="preview-slide-navigation preview-slide-navigation-top"
          style={{
            transform: `translate(calc(-50% + ${indicatorOffsetX}px), calc(-100% - 16px + ${indicatorOffsetY}px))`
          }}
        >
          {validSlides.map((_, index) => {
            const displayIndex = ((currentSlideIndex % totalSlides) + totalSlides) % totalSlides;
            return (
              <button
                key={index}
                className={`preview-nav-dot ${index === displayIndex ? 'active' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentSlideIndex(index);
                }}
                aria-label={`スライド ${index + 1} に移動`}
              />
            );
          })}
        </div>
      )}

      {validSlides.length > 1 && indicatorPosition === 'bottom' && (
        <div 
          className="preview-slide-navigation preview-slide-navigation-bottom"
          style={{
            transform: `translate(calc(-50% + ${indicatorOffsetX}px), calc(100% + 16px + ${indicatorOffsetY}px))`
          }}
        >
          {validSlides.map((_, index) => {
            const displayIndex = ((currentSlideIndex % totalSlides) + totalSlides) % totalSlides;
            return (
              <button
                key={index}
                className={`preview-nav-dot ${index === displayIndex ? 'active' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentSlideIndex(index);
                }}
                aria-label={`スライド ${index + 1} に移動`}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};
